import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  Link as LinkIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';
import { phoneExampleE164, sendDocumentPhoneHelperText } from '../utils/phoneRegional';
import {
  PRESCRIPTION_INBOX_PROVIDER_LABEL,
  type ClinicalDocumentWhatsAppKind,
  useClinicalDocumentWhatsAppInbox,
} from '../utils/prescriptionWhatsAppInbox';

const KL_TEAL = '#0D9488';
const KL_TEAL_DARK = '#0F766E';
const KL_WA = '#25D366';

interface SendDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documentType: string;
  documentId: string;
  patientId?: string;
  patientEmail?: string;
  patientPhone?: string;
  patientWhatsapp?: string;
  patientName?: string;
  documentLabel?: string;
  /** Vários documentos do mesmo paciente num link só. */
  documentIds?: string[];
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  prescription: 'Receituário',
  certificate: 'Atestado',
  referral: 'Encaminhamento',
  budget: 'Orçamento',
  receipt: 'Recibo',
  evolution: 'Evolução clínica',
  nfse: 'Nota fiscal',
  report: 'Relatório',
  exam_order: 'Pedido de exames',
  appointment: 'Comprovante de agendamento',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    '&:hover fieldset': { borderColor: alpha(KL_TEAL, 0.5) },
    '&.Mui-focused fieldset': { borderColor: KL_TEAL },
  },
};

const SendDocumentDialog: React.FC<SendDocumentDialogProps> = ({
  open,
  onClose,
  documentType,
  documentId,
  patientId,
  patientEmail,
  patientPhone,
  patientWhatsapp,
  patientName,
  documentLabel,
  documentIds,
}) => {
  const bundleIds = documentIds && documentIds.length > 1 ? documentIds : null;
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [waPhone, setWaPhone] = useState('');
  const [waCaption, setWaCaption] = useState('');
  const [resolvedName, setResolvedName] = useState('');

  const whatsappInboxKind: ClinicalDocumentWhatsAppKind | null =
    documentType === 'prescription' || documentType === 'certificate' || documentType === 'exam_order'
      ? documentType
      : null;
  const documentInbox = useClinicalDocumentWhatsAppInbox(
    whatsappInboxKind || 'prescription',
    open && Boolean(whatsappInboxKind),
  );
  const waChannelId = whatsappInboxKind ? documentInbox.channelId : '';
  const waSelectedProvider = whatsappInboxKind ? documentInbox.selected?.provider : undefined;
  const isPrescription = documentType === 'prescription';

  useEffect(() => {
    if (!open) return;
    setWaCaption('');
    setSending(false);
    setResolvedName(patientName || '');
    if (patientId) {
      void loadPatientContactData(patientId);
    } else {
      setWaPhone(patientWhatsapp || patientPhone || '');
    }
  }, [open, patientId, patientPhone, patientWhatsapp, patientName]);

  const loadPatientContactData = async (pid: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/patients/${pid}`));
      const patient = data?.data || data;
      if (patient) {
        setWaPhone(patient.whatsapp || patient.phone || patientWhatsapp || patientPhone || '');
        if (patient.full_name) setResolvedName(patient.full_name);
      }
    } catch {
      setWaPhone(patientWhatsapp || patientPhone || '');
    }
  };

  const normalizePhone = (phone: string): string => {
    const clean = phone.replace(/\D/g, '');
    if (clean.startsWith('55') && clean.length >= 12) return clean;
    if (clean.length === 11 || clean.length === 10) return `55${clean}`;
    return clean;
  };

  const handleSendWhatsApp = async () => {
    if (!waPhone.trim()) return;
    setSending(true);
    try {
      const phoneToSend = normalizePhone(waPhone.trim());
      if (bundleIds) {
        const { data } = await axios.post(getApiUrl('/clinic/documents/send-whatsapp-bundle'), {
          type: documentType,
          ids: bundleIds,
          phone: phoneToSend,
          caption: waCaption || undefined,
          channel_id: waChannelId || undefined,
        });
        setSnackbar({ open: true, message: data?.message || 'Link enviado pelo WhatsApp.', severity: 'success' });
        setTimeout(onClose, 1400);
        return;
      }
      await axios.post(getApiUrl('/clinic/documents/send-whatsapp'), {
        type: documentType,
        id: documentId,
        phone: phoneToSend,
        caption: waCaption || undefined,
        channel_id: waChannelId || undefined,
      });
      setSnackbar({ open: true, message: 'Documento enviado pelo WhatsApp.', severity: 'success' });
      setTimeout(onClose, 1400);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Não foi possível enviar pelo WhatsApp.',
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const typeLabel = bundleIds
    ? `${bundleIds.length} ${isPrescription ? 'receitas' : 'documentos'} · um link`
    : documentLabel || DOCUMENT_TYPE_LABELS[documentType] || documentType;

  const deliveryHint = bundleIds
    ? 'O paciente recebe um único link e escolhe qual documento abrir.'
    : waSelectedProvider === 'cloud_api'
      ? 'Envio pela API oficial, com template aprovado e botão de link.'
      : waSelectedProvider === 'zapi'
        ? 'Envio pela Z-API com PDF ou link, conforme a instância.'
        : 'Cloud API usa template aprovado; Z-API envia o PDF direto na conversa.';

  return (
    <>
      <Dialog
        open={open}
        onClose={sending ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            borderTop: `4px solid ${KL_TEAL}`,
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, pt: 2.5, pb: 2, bgcolor: '#F8FAFC' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(KL_WA, 0.12),
                  color: KL_WA,
                  flexShrink: 0,
                }}
              >
                <WhatsAppIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0F172A', lineHeight: 1.25 }}>
                  Enviar ao paciente
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25 }} noWrap>
                  {typeLabel}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={sending}
              size="small"
              sx={{ color: '#94A3B8', mt: -0.5 }}
              aria-label="Fechar"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: '#fff' }}>
          {(resolvedName || patientId) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 2.5,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#F0FDFA',
              }}
            >
              <PersonIcon sx={{ color: KL_TEAL, fontSize: 22 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, letterSpacing: 0.3 }}>
                  DESTINATÁRIO
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#0F172A' }} noWrap>
                  {resolvedName || 'Paciente'}
                </Typography>
              </Box>
              {bundleIds && (
                <Box
                  sx={{
                    ml: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: '#CCFBF1',
                    color: KL_TEAL_DARK,
                  }}
                >
                  <LinkIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Link único
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Alert
            severity="info"
            icon={<WhatsAppIcon fontSize="small" />}
            sx={{
              mb: 2,
              borderRadius: 2,
              bgcolor: '#F0FDF4',
              color: '#166534',
              border: '1px solid #BBF7D0',
              '& .MuiAlert-icon': { color: KL_WA },
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
              {deliveryHint}
            </Typography>
          </Alert>

          {whatsappInboxKind && (
            <FormControl fullWidth size="small" sx={{ mb: 2, ...fieldSx }}>
              <InputLabel>Inbox WhatsApp</InputLabel>
              <Select
                value={documentInbox.selected ? waChannelId : ''}
                label="Inbox WhatsApp"
                onChange={(e) => documentInbox.setChannelId(String(e.target.value))}
                disabled={documentInbox.loading}
              >
                <MenuItem value="">Automático (padrão da unidade)</MenuItem>
                {documentInbox.inboxes.map((inbox) => (
                  <MenuItem key={inbox.channelId} value={inbox.channelId}>
                    {inbox.name} · {PRESCRIPTION_INBOX_PROVIDER_LABEL[inbox.provider]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="WhatsApp do paciente"
            required
            value={waPhone}
            onChange={(e) => setWaPhone(e.target.value)}
            placeholder={phoneExampleE164()}
            size="small"
            sx={{ mb: 2, ...fieldSx }}
            helperText={sendDocumentPhoneHelperText()}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Mensagem (opcional)"
            placeholder={
              bundleIds
                ? 'Texto antes do link (Z-API; na Cloud API o template define a mensagem).'
                : 'Legenda na Z-API; na Cloud API o template costuma ignorar este campo.'
            }
            value={waCaption}
            onChange={(e) => setWaCaption(e.target.value)}
            sx={fieldSx}
          />

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#94A3B8', lineHeight: 1.5 }}>
            Templates em Configurações → WhatsApp. Receitas controladas precisam estar assinadas antes do envio.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            bgcolor: '#F8FAFC',
            borderTop: '1px solid #E2E8F0',
          }}
        >
          <Button
            onClick={onClose}
            disabled={sending}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B', borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            disableElevation
            onClick={() => void handleSendWhatsApp()}
            disabled={sending || !waPhone.trim()}
            startIcon={
              sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <WhatsAppIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              bgcolor: KL_WA,
              '&:hover': { bgcolor: '#1EBE5A' },
              '&.Mui-disabled': { bgcolor: alpha(KL_WA, 0.4), color: '#fff' },
            }}
          >
            {sending ? 'Enviando…' : 'Enviar pelo WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SendDocumentDialog;
